/**
 * Hook to connect to the per-user asset library YDoc room.
 *
 * Connects to /ws/yjs/_library/default via WebSocket and returns
 * library nodes as typed ReactFlow canvas nodes. Only connects when enabled=true.
 */

import { useEffect, useRef, useState } from 'react'
import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'

/** Access internal wsLastMessageReceived on WebsocketProvider (not in public types). */
function getWsLastMessageReceived(provider: WebsocketProvider): number | undefined {
  return provider.wsLastMessageReceived
}
function setWsLastMessageReceived(provider: WebsocketProvider, value: number): void {
  provider.wsLastMessageReceived = value
}
import { getConfiguredApiBase, getAuthToken, type YDocValue } from '@ponpon/api-client'
import { readNodeDataFromYMap, type ReactFlowCanvasNode } from './useYCanvas'
export interface UseLibraryYDocOptions {
  /** Only connect when true. Set to false to disconnect and save resources. */
  enabled?: boolean
}

export interface UseLibraryYDocResult {
  /** Library nodes as ReactFlow Node objects */
  nodes: ReactFlowCanvasNode[]
  /** Whether initial sync with server is complete */
  synced: boolean
  /** Whether WebSocket connection is live */
  connected: boolean
}

function yString(map: Y.Map<YDocValue>, key: string, fallback: string): string {
  const value = map.get(key)
  return typeof value === 'string' ? value : fallback
}

function yNumber(map: Y.Map<YDocValue>, key: string, fallback: number): number {
  const value = map.get(key)
  return typeof value === 'number' ? value : fallback
}

/**
 * Convert an HTTP(S) API base URL to a WebSocket URL.
 */
function toWsUrl(httpUrl: string): string {
  return httpUrl.replace(/^http/, 'ws')
}

/** Liveness watchdog matching useYDoc.ts. ~5s detection of silent
 *  disconnects (server killed but TCP/LB don't surface the close). */
const HEARTBEAT_INTERVAL_MS = 2000
const HEARTBEAT_DEAD_MS = 5000

export function useLibraryYDoc({
  enabled = true,
}: UseLibraryYDocOptions = {}): UseLibraryYDocResult {
  const [nodes, setNodes] = useState<ReactFlowCanvasNode[]>([])
  const [synced, setSynced] = useState(false)
  const [connected, setConnected] = useState(false)
  const ydocRef = useRef<Y.Doc | null>(null)
  const providerRef = useRef<WebsocketProvider | null>(null)

  useEffect(() => {
    if (!enabled) {
      // Clean up if disabled
      if (providerRef.current) {
        providerRef.current.destroy()
        providerRef.current = null
      }
      if (ydocRef.current) {
        ydocRef.current.destroy()
        ydocRef.current = null
      }
      setNodes([])
      setSynced(false)
      setConnected(false)
      return
    }

    const ydoc = new Y.Doc()
    ydocRef.current = ydoc
    let cancelled = false
    let watchdogTimer: ReturnType<typeof setInterval> | null = null

    const stopWatchdog = () => {
      if (watchdogTimer != null) {
        clearInterval(watchdogTimer)
        watchdogTimer = null
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && providerRef.current) {
        setWsLastMessageReceived(providerRef.current, Date.now())
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    const connect = async () => {
      const token = await getAuthToken()
      if (cancelled) return

      const apiBase = getConfiguredApiBase()
      const wsBase = toWsUrl(apiBase)
      // Library endpoint: /ws/yjs/_library with room name "default"
      // The server constructs the full room name library:{user_id}/default from the JWT
      const wsUrl = `${wsBase}/ws/yjs/_library`
      const roomName = 'default'

      const params: Record<string, string> = {}
      if (token) {
        params['token'] = token
      }

      const provider = new WebsocketProvider(wsUrl, roomName, ydoc, {
        connect: true,
        params,
        // Sync messages drive inbound traffic for the watchdog (every 2s
        // so a 5s dead-timeout has enough samples to detect silent
        // disconnects within ~5s).
        resyncInterval: 2000,
        // Cap above the y-websocket default of 2.5s so transient blips
        // don't hammer the server with reconnect attempts.
        maxBackoffTime: 5000,
      })

      provider.on('status', async ({ status }: { status: string }) => {
        if (cancelled) return
        setConnected(status === 'connected')
        if (status === 'disconnected') {
          // y-websocket stringifies ``params`` once at constructor time
          // and reuses the same query string on every auto-reconnect.
          // After 1h the Firebase ID token in ``?token=`` expires and the
          // server starts rejecting every reconnect with 4003 — the
          // client loops forever logging "invalid/expired auth token".
          // Refresh the token in-place so the next reconnect attempt
          // carries a fresh one. Matches the useYDoc.ts pattern.
          try {
            const freshToken = await getAuthToken()
            if (freshToken && provider?.params) {
              provider.params['token'] = freshToken
            }
          } catch {
            // Token refresh shouldn't crash the canvas — fall through
            // and let the reconnect surface the auth error organically.
          }
        }
      })

      provider.on('sync', (isSynced: boolean) => {
        setSynced(isSynced)
      })

      providerRef.current = provider

      // Liveness watchdog: force-close + reconnect if no inbound for 5s.
      // y-websocket's onclose will then schedule its automatic reconnect.
      stopWatchdog()
      watchdogTimer = setInterval(() => {
        if (cancelled || !provider) return
        const lastMs = getWsLastMessageReceived(provider)
        if (lastMs && Date.now() - lastMs > HEARTBEAT_DEAD_MS) {
          if (provider.ws) {
            try { provider.ws.close() } catch { /* already closed */ }
          }
        }
      }, HEARTBEAT_INTERVAL_MS)

      // Observe the 'nodes' map for changes and rebuild ReactFlow nodes
      const yNodes = ydoc.getMap<Y.Map<YDocValue>>('nodes')

      const rebuildNodes = () => {
        const result: ReactFlowCanvasNode[] = []
        yNodes.forEach((ynode: Y.Map<YDocValue>, nodeId: string) => {
          const nodeType = yString(ynode, 'type', '')
          const dataRaw = readNodeDataFromYMap(ynode)

          const node: ReactFlowCanvasNode = {
            id: nodeId,
            type: nodeType,
            position: {
              x: yNumber(ynode, 'x', 0),
              y: yNumber(ynode, 'y', 0),
            },
            data: {
              ...dataRaw,
              status: yString(ynode, 'status', 'idle'),
              label: yString(ynode, 'label', nodeId),
            },
          }
          result.push(node)
        })
        setNodes(result)
      }

      yNodes.observeDeep(rebuildNodes)
      // Initial build once connected
      rebuildNodes()
    }

    connect()

    return () => {
      cancelled = true
      stopWatchdog()
      document.removeEventListener('visibilitychange', onVisibilityChange)
      if (providerRef.current) {
        providerRef.current.destroy()
        providerRef.current = null
      }
      if (ydocRef.current) {
        ydocRef.current.destroy()
        ydocRef.current = null
      }
      setConnected(false)
      setSynced(false)
      setNodes([])
    }
  }, [enabled])

  return { nodes, synced, connected }
}
